// Admin API wrapper — same shape as the trader client's wrapper but
// targets /admin/* by default. Cookie auth + CSRF token replay is
// identical: the browser ships sm_session automatically and we
// stash the csrf_token returned by /admin/login (or /admin/login/mfa)
// in localStorage so subsequent state-changing requests can replay
// it on the X-CSRF-Token header.

const CSRF_KEY = "sm_admin_csrf";

export class APIError extends Error {
  status: number;
  code: string;
  body: unknown;
  /** The failing response's headers (X-Request-ID and friends), when there was a response. */
  headers?: Headers;
  /** The request's path, so error copy can tell which console area refused (SM-958). */
  path?: string;
  constructor(status: number, code: string, message: string, body: unknown, headers?: Headers, path?: string) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
    this.headers = headers;
    this.path = path;
  }
}

export function getCSRF(): string | null {
  return typeof window !== "undefined" ? window.localStorage.getItem(CSRF_KEY) : null;
}

export function setCSRF(token: string | null) {
  if (typeof window === "undefined") return;
  if (token) window.localStorage.setItem(CSRF_KEY, token);
  else window.localStorage.removeItem(CSRF_KEY);
}

export interface ApiOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE" | "PUT";
  body?: unknown;
  signal?: AbortSignal;
  skipCSRF?: boolean;
  // expectMFAChallenge lets the login flow surface the 428 NeedsMFA
  // branch as a returned object instead of throwing. The gateway uses
  // RFC-6585 428 Precondition Required (Go's http.StatusPreconditionRequired)
  // to signal "password OK, second factor needed" — the caller
  // pattern-matches on `needs_mfa` in the response body.
  expectMFAChallenge?: boolean;
  // idempotencyKey makes a create replayable: the server keys its
  // duplicate guard on it, so re-sending the same key returns the
  // original row instead of creating a second one (SM-347). Mint one key
  // per user-intent — reusing it across two deliberate creates would make
  // the second silently return the first.
  idempotencyKey?: string;
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  return (await request<T>(path, opts)).data;
}

/** A response body together with the headers it came with. */
export interface ApiResponse<T> {
  data: T;
  headers: Headers;
}

/**
 * api, keeping the response headers. For the calls whose answer is also
 * in a header — broker-service names the audit events a mutation wrote
 * in X-Broker-Audit-Event-Ids (SM-716 AC), which the console shows the
 * operator after a confirmed action. Errors throw exactly as api does.
 */
export function apiWithHeaders<T = unknown>(path: string, opts: ApiOptions = {}): Promise<ApiResponse<T>> {
  return request<T>(path, opts);
}

async function request<T>(path: string, opts: ApiOptions): Promise<ApiResponse<T>> {
  const method = opts.method ?? "GET";
  const headers: Record<string, string> = { Accept: "application/json" };
  // FormData → let the browser set Content-Type (with the boundary)
  // and pass the body through unchanged. Anything else: JSON-encode.
  const isFormData = typeof FormData !== "undefined" && opts.body instanceof FormData;
  if (opts.body !== undefined && !isFormData) headers["Content-Type"] = "application/json";
  if (!opts.skipCSRF && method !== "GET") {
    const tok = getCSRF();
    if (tok) headers["X-CSRF-Token"] = tok;
  }
  if (opts.idempotencyKey) headers["Idempotency-Key"] = opts.idempotencyKey;
  const res = await fetch(path, {
    method,
    headers,
    credentials: "include",
    body: opts.body !== undefined
      ? (isFormData ? (opts.body as FormData) : JSON.stringify(opts.body))
      : undefined,
    signal: opts.signal,
  });
  if (res.status === 204) return { data: undefined as T, headers: res.headers };
  let json: unknown = null;
  const text = await res.text();
  if (text) {
    try { json = JSON.parse(text); } catch { json = text; }
  }
  if (res.status === 428 && opts.expectMFAChallenge) {
    return { data: json as T, headers: res.headers }; // caller wants this status surfaced
  }
  if (!res.ok) {
    const err = (json as { error?: { code?: string; message?: string } })?.error;
    throw new APIError(
      res.status,
      err?.code ?? "UNKNOWN",
      err?.message ?? `HTTP ${res.status}`,
      json,
      res.headers,
      path,
    );
  }
  return { data: json as T, headers: res.headers };
}

// newIdempotencyKey mints one key for one user intent (SM-347). Pass it
// as ApiOptions.idempotencyKey on a create so a duplicate delivery of the
// same request returns the original row instead of a second one. Mint it
// when the intent starts — opening the dialog — not per attempt, so a
// retry after a lost response replays instead of duplicating.
export function newIdempotencyKey(): string {
  // randomUUID needs a secure context; the admin console is https (or
  // localhost in dev), but fall back rather than throw on the odd
  // non-secure origin — a weaker key still scopes to one dialog session.
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

export function qs(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === "" || v === null) continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : "";
}
